import type { HandbookTrainingOutlineChapter } from '../types/handbookTraining';
import { normalizeHandbookTrainingSlug } from './handbookTrainingAccess';
import { parseLoomVideoId } from './loomEmbed';

export type HandbookTrainingAgentMetadataSuggestion = {
  slug: string;
  recordedAt?: string;
  durationSec?: number;
  routes: string[];
  features: string[];
  apis: string[];
  staleIf: string[];
  outline: HandbookTrainingOutlineChapter[];
};

type TopicHint = {
  test: RegExp;
  routes?: string[];
  features?: string[];
  apis?: string[];
};

/** Keyword → CRM coverage hints for People Hub training walkthroughs. */
const TOPIC_HINTS: TopicHint[] = [
  {
    test: /travel|trip|itinerary|planner|calendar sync/i,
    routes: ['/people/travel', '/people/travel/:tripId'],
    features: ['PeopleTravelPlannerBoard', 'tripPlanFromPrompt', 'PeopleTravelAgentStatusBanner'],
    apis: ['travelAgentApi'],
  },
  {
    test: /handbook|training video|training library|policy/i,
    routes: ['/people/handbook', '/people/handbook/training', '/people/handbook/training/:nodeId'],
    features: ['handbookTrainingLibrary', 'HandbookHubTabs'],
    apis: ['handbookTrainingAgentApi'],
  },
  {
    test: /benefit|401|health|pto|time off/i,
    routes: ['/people/benefits'],
    features: ['PeopleBenefits'],
    apis: [],
  },
  {
    test: /hiring|job|recruit|offer/i,
    routes: ['/people/hiring'],
    features: ['PeopleHiring'],
    apis: [],
  },
  {
    test: /org chart|organization|reporting/i,
    routes: ['/people/org'],
    features: ['PeopleOrgChart'],
    apis: [],
  },
  {
    test: /achievement|recognition|kudos/i,
    routes: ['/people/achievements'],
    features: ['PeopleAchievements'],
    apis: [],
  },
  {
    test: /contractor|timesheet|1099/i,
    routes: ['/people'],
    features: ['contractorTimesheets', 'PeopleHub'],
    apis: ['opsTasksAgentApi'],
  },
  {
    test: /finance|quickbooks|payroll|overhead|margin/i,
    routes: ['/finances'],
    features: ['FinanceHub'],
    apis: ['financeAgentApi'],
  },
  {
    test: /proposal|precon|design estimating/i,
    routes: ['/proposal-builder/:dealId', '/proposals'],
    features: ['ProposalBuilder', 'designEstimating'],
    apis: ['proposalAgentApi', 'financeAgentApi'],
  },
  {
    test: /project hub|timber project|time track/i,
    routes: ['/project-hub', '/project-hub/:projectId'],
    features: ['ProjectHub', 'timeTracking'],
    apis: ['projectsAgentApi'],
  },
  {
    test: /deal|pipeline|lead|sales flow/i,
    routes: ['/pipeline', '/sales-flow', '/leads'],
    features: ['SalesFlow', 'LeadsHub'],
    apis: ['dealsAgentApi', 'salesAgentApi'],
  },
  {
    test: /marketing|meta ads|utm|competitor/i,
    routes: ['/analytics/meta', '/utm-strategy'],
    features: ['MarketingHub'],
    apis: ['marketingAgentApi'],
  },
  {
    test: /meeting notes|fathom/i,
    routes: ['/project-hub/:projectId'],
    features: ['MeetingNotes'],
    apis: ['meetingNotesAgentApi'],
  },
  {
    test: /bid|bidding|qualification/i,
    routes: ['/leads'],
    features: ['BiddingQueue'],
    apis: ['biddingAgentApi'],
  },
  {
    test: /material|timber list|hardware price/i,
    routes: ['/materials'],
    features: ['MaterialsDatabase'],
    apis: ['materialsPriceAgentApi', 'timberListAgentApi'],
  },
  {
    test: /agent harness|agent activity|company brain/i,
    routes: ['/agent-harness', '/agent-activity'],
    features: ['AgentHarness', 'AgentActivity'],
    apis: ['agentGatewayApi'],
  },
  {
    test: /feedback|bug report|crm feedback/i,
    routes: ['/settings/feedback'],
    features: ['CrmFeedback'],
    apis: ['crmFeedbackAgentApi'],
  },
  {
    test: /trade briefing|trade portal|subcontractor briefing|millwork invite/i,
    routes: ['/project-hub/:projectId', '/briefing/:token', '/briefings'],
    features: ['TradeBriefingLinks', 'TradeBriefingPage'],
    apis: ['tradeBriefingAgentApi'],
  },
];

const AGENT_NAME_IN_TEXT: { pattern: RegExp; apiId: string }[] = [
  { pattern: /\btravel\s*agent\b/i, apiId: 'travelAgentApi' },
  { pattern: /\bfinance\s*agent\b/i, apiId: 'financeAgentApi' },
  { pattern: /\bproposal\s*agent\b/i, apiId: 'proposalAgentApi' },
  { pattern: /\bdeals\s*agent\b/i, apiId: 'dealsAgentApi' },
  { pattern: /\bhandbook\s*training\s*agent\b/i, apiId: 'handbookTrainingAgentApi' },
  { pattern: /\btrade\s*briefing\s*agent\b/i, apiId: 'tradeBriefingAgentApi' },
];

function uniqSorted(values: string[]): string[] {
  return Array.from(new Set(values.map(v => v.trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  );
}

export function ensureUniqueHandbookTrainingSlug(base: string, taken: string[], exceptSlug?: string): string {
  const norm = normalizeHandbookTrainingSlug(base);
  if (!norm) return '';
  const blocked = new Set(
    taken.map(s => normalizeHandbookTrainingSlug(s)).filter(Boolean),
  );
  const self = exceptSlug ? normalizeHandbookTrainingSlug(exceptSlug) : '';
  if (self) blocked.delete(self);
  if (!blocked.has(norm)) return norm;
  for (let n = 2; n < 100; n++) {
    const candidate = `${norm}-${n}`;
    if (!blocked.has(candidate)) return candidate;
  }
  return `${norm}-${Date.now()}`;
}

export function buildStaleIfFromCoverage(features: string[], apis: string[]): string[] {
  const lines: string[] = [];
  for (const api of apis) {
    lines.push(`${api} major/minor`);
  }
  for (const f of features) {
    lines.push(`${f} UI`);
  }
  return uniqSorted(lines);
}

function defaultOutline(title: string): HandbookTrainingOutlineChapter[] {
  const label = title.trim() || 'Overview';
  return [{ id: 'intro', title: label, t: 0 }];
}

function matchHints(haystack: string): Pick<HandbookTrainingAgentMetadataSuggestion, 'routes' | 'features' | 'apis'> {
  const routes: string[] = [];
  const features: string[] = [];
  const apis: string[] = [];
  for (const hint of TOPIC_HINTS) {
    if (!hint.test.test(haystack)) continue;
    if (hint.routes) routes.push(...hint.routes);
    if (hint.features) features.push(...hint.features);
    if (hint.apis) apis.push(...hint.apis);
  }
  for (const row of AGENT_NAME_IN_TEXT) {
    if (row.pattern.test(haystack)) apis.push(row.apiId);
  }
  return { routes: uniqSorted(routes), features: uniqSorted(features), apis: uniqSorted(apis) };
}

export type SuggestHandbookTrainingAgentMetadataInput = {
  title: string;
  description?: string;
  breadcrumbTitles?: string[];
  loomUrl?: string;
  existingSlugs: string[];
  currentSlug?: string;
  /** ISO date fallback when Loom has no upload date (e.g. new draft). */
  defaultRecordedAt?: string;
  loomDurationSec?: number;
  loomRecordedAt?: string;
};

/** Pure suggestion used by the CRM editor and save path. */
export function suggestHandbookTrainingAgentMetadata(
  input: SuggestHandbookTrainingAgentMetadataInput,
): HandbookTrainingAgentMetadataSuggestion {
  const title = input.title.trim();
  const haystack = [title, input.description || '', ...(input.breadcrumbTitles || [])].join('\n');
  const { routes, features, apis } = matchHints(haystack);
  const slug = ensureUniqueHandbookTrainingSlug(
    title,
    input.existingSlugs,
    input.currentSlug,
  );
  const recordedAt =
    input.loomRecordedAt ||
    input.defaultRecordedAt ||
    new Date().toISOString().slice(0, 10);
  const durationSec =
    input.loomDurationSec && input.loomDurationSec > 0 ? Math.round(input.loomDurationSec) : undefined;
  const staleIf = buildStaleIfFromCoverage(features, apis);
  const outline = defaultOutline(title);
  return { slug, recordedAt, durationSec, routes, features, apis, staleIf, outline };
}

export type LoomOembedMetadata = {
  title?: string;
  durationSec?: number;
  recordedAt?: string;
};

/** Best-effort Loom oEmbed (browser or Node fetch). Returns null on failure. */
export async function fetchLoomOembedMetadata(loomUrl: string): Promise<LoomOembedMetadata | null> {
  const id = parseLoomVideoId(loomUrl);
  if (!id) return null;
  const shareUrl = `https://www.loom.com/share/${id}`;
  const endpoint = `https://www.loom.com/v1/oembed?url=${encodeURIComponent(shareUrl)}`;
  try {
    const res = await fetch(endpoint);
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    const title = typeof data.title === 'string' ? data.title : undefined;
    let durationSec: number | undefined;
    if (typeof data.duration === 'number' && data.duration > 0) {
      durationSec = data.duration;
    } else if (typeof data.duration === 'string') {
      const n = parseFloat(data.duration);
      if (Number.isFinite(n) && n > 0) durationSec = n;
    }
    let recordedAt: string | undefined;
    const uploaded =
      typeof data.uploaded_at === 'string'
        ? data.uploaded_at
        : typeof data.upload_date === 'string'
          ? data.upload_date
          : undefined;
    if (uploaded) {
      const d = new Date(uploaded);
      if (!Number.isNaN(d.getTime())) recordedAt = d.toISOString().slice(0, 10);
    }
    return { title, durationSec, recordedAt };
  } catch {
    return null;
  }
}

export function tagsFromList(list: string[] | undefined): string {
  return (list || []).join(', ');
}
